import { forwardRef } from 'react';
import Button, { type ButtonProps } from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';

export interface AppButtonProps extends Omit<ButtonProps, 'variant'> {
  /**
   * Button variant
   * - primary: Filled blue button for primary actions
   * - secondary: Outlined button for secondary actions
   * - danger: Red button for destructive actions
   * - success: Green button for positive actions
   * - ghost: Transparent button with border
   */
  variant?: 'primary' | 'secondary' | 'danger' | 'success' | 'ghost';
  /**
   * Show loading spinner and disable button
   */
  loading?: boolean;
  /**
   * Icon to show before the label
   */
  startIcon?: React.ReactNode;
  /**
   * Icon to show after the label
   */
  endIcon?: React.ReactNode;
}

/**
 * AppButton - Standard button component
 *
 * Wraps MUI Button with our design system variants.
 * Use this for all button needs instead of raw MUI Button.
 */
export const AppButton = forwardRef<HTMLButtonElement, AppButtonProps>(
  ({ variant = 'primary', loading = false, disabled, children, startIcon, endIcon, ...props }, ref) => {
    // Map our variants to MUI props
    const getMuiProps = (): Partial<ButtonProps> => {
      switch (variant) {
        case 'primary':
          return { variant: 'contained', color: 'primary' };
        case 'secondary':
          return { variant: 'outlined', color: 'primary' };
        case 'danger':
          return { variant: 'contained', color: 'error' };
        case 'success':
          return { variant: 'contained', color: 'success' };
        case 'ghost':
          return { variant: 'ghost' as ButtonProps['variant'] };
        default:
          return { variant: 'contained', color: 'primary' };
      }
    };

    return (
      <Button
        ref={ref}
        {...getMuiProps()}
        disabled={disabled || loading}
        startIcon={loading ? <CircularProgress size={16} color="inherit" /> : startIcon}
        endIcon={endIcon}
        {...props}
      >
        {children}
      </Button>
    );
  }
);

AppButton.displayName = 'AppButton';
